import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../components/CustomSelect";
import ClubMultiSelect from "../../components/admin/shared/ClubMultiSelect";
import { createCompetition } from "../../api/admin";
import { Club, getAllClubs } from "../../api/clubs";
import { card, primaryBtn, secondaryBtn, inputStyle, panelHeading, errorStyle } from "../../styles";

interface Props {
  user: User;
}

type Stage = "identity" | "gameweeks" | "review";

export default function CreateCompetitionPage({ user }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("identity");

  const [name, setName] = useState("");
  const [season, setSeason] = useState("");
  const [type, setType] = useState<"CLUB" | "INTERNATIONAL">("CLUB");

  const [gameweekCount, setGameweekCount] = useState(38);
  const [gwNames, setGwNames] = useState<Record<number, string>>({});
  const [gwShortNames, setGwShortNames] = useState<Record<number, string>>({});

  const [allClubs, setAllClubs] = useState<Club[]>([]);
  const [selectedClubIds, setSelectedClubIds] = useState<string[]>([]);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getAllClubs(user).then(setAllClubs).catch(() => {});
  }, [user]);

  const seasonOptions = Array.from({ length: 8 }, (_, i) => {
    const startYear = new Date().getFullYear() - 1 + i;
    const s = `${startYear}/${String(startYear + 1).slice(-2)}`;
    return { value: s, label: s };
  });

  const identityValid = !!name.trim() && !!season;
  const gameweeksValid = gameweekCount >= 1;

  const handleCreate = async () => {
    setCreating(true);
    setError("");
    try {
      const created = await createCompetition(user, {
        name: name.trim(),
        season,
        type,
        supportedModes: [],
        gameweekCount,
        gameweekNames: gwNames,
        gameweekShortNames: gwShortNames,
        ...(selectedClubIds.length > 0 ? { clubIds: selectedClubIds } : {}),
      });
      navigate(`/admin/competitions/${created.id}`);
    } catch {
      setError(t("admin.competitions.errors.failedToCreate"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ maxWidth: "640px" }}>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/admin/competitions" style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", textDecoration: "none" }}>
          ← {t("admin.competitions.title")}
        </Link>
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
        {(["identity", "gameweeks", "review"] as Stage[]).map((s, i) => (
          <div key={s} style={{
            flex: 1, textAlign: "center", padding: "0.5rem", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600,
            background: stage === s ? "var(--color-accent)" : "var(--color-surface-alt)",
            color: stage === s ? "#fff" : "var(--color-text-muted)",
          }}>
            {i + 1}. {t(`admin.competitions.createFlow.stages.${s}`)}
          </div>
        ))}
      </div>

      <div style={card}>
        {error && <p style={errorStyle}>{error}</p>}

        {stage === "identity" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.competitions.createFlow.stages.identity")}</h2>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
                {t("admin.competitions.createModal.nameLabel")}
              </label>
              <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
                {t("admin.competitions.createModal.seasonLabel")}
              </label>
              <CustomSelect value={season} onChange={setSeason} placeholder="— Select season —" options={seasonOptions} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
                {t("admin.competitions.createModal.typeLabel")}
              </label>
              <CustomSelect
                value={type}
                onChange={(val) => setType(val as "CLUB" | "INTERNATIONAL")}
                options={[
                  { value: "CLUB", label: t("admin.competitions.type.club") },
                  { value: "INTERNATIONAL", label: t("admin.competitions.type.international") },
                ]}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button style={primaryBtn} disabled={!identityValid} onClick={() => setStage("gameweeks")}>
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {stage === "gameweeks" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.competitions.createFlow.stages.gameweeks")}</h2>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-dim)" }}>{t("admin.competitions.createFlow.gameweekBanner")}</p>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>
                {t("admin.competitions.createModal.gameweekCountLabel")}
              </label>
              <input
                type="number"
                min={1}
                style={{ ...inputStyle, maxWidth: "140px" }}
                value={gameweekCount}
                onChange={(e) => setGameweekCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.4rem" }}>
                {t("admin.competitions.createFlow.gwNamesLabel")}
              </label>
              <div style={{ maxHeight: "220px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.4rem", border: "1px solid var(--color-border)", borderRadius: "10px", padding: "0.5rem" }}>
                {Array.from({ length: gameweekCount }, (_, i) => i + 1).map((n) => (
                  <div key={n} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-dim)", width: "3rem", flexShrink: 0 }}>GW{n}</span>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder={`GW${n}`}
                      value={gwNames[n] ?? ""}
                      onChange={(e) => setGwNames((prev) => ({ ...prev, [n]: e.target.value }))}
                    />
                    <input
                      style={{ ...inputStyle, width: "90px" }}
                      placeholder={t("admin.competitions.createFlow.shortNamePlaceholder")}
                      maxLength={4}
                      value={gwShortNames[n] ?? ""}
                      onChange={(e) => setGwShortNames((prev) => ({ ...prev, [n]: e.target.value.toUpperCase() }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.4rem" }}>
                {t("admin.competitions.createModal.clubsLabel")}
                {selectedClubIds.length > 0 && (
                  <span style={{ marginLeft: "0.5rem", color: "var(--color-accent)", fontWeight: 600 }}>
                    ({selectedClubIds.length} {t("admin.competitions.createFlow.selected")})
                  </span>
                )}
              </label>
              <ClubMultiSelect
                clubs={allClubs}
                selectedClubIds={selectedClubIds}
                onChange={setSelectedClubIds}
                clearSelectionLabel={t("admin.competitions.entrants.addModal.clearSelection")}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={secondaryBtn} onClick={() => setStage("identity")}>{t("common.back")}</button>
              <button style={primaryBtn} disabled={!gameweeksValid} onClick={() => setStage("review")}>{t("common.next")}</button>
            </div>
          </div>
        )}

        {stage === "review" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("admin.competitions.createFlow.stages.review")}</h2>
            <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", rowGap: "0.4rem", columnGap: "0.75rem", fontSize: "0.88rem" }}>
              <dt style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.createModal.nameLabel")}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{name}</dd>
              <dt style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.createModal.seasonLabel")}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{season}</dd>
              <dt style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.createModal.typeLabel")}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{type === "CLUB" ? t("admin.competitions.type.club") : t("admin.competitions.type.international")}</dd>
              <dt style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.createModal.gameweekCountLabel")}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{gameweekCount}</dd>
              <dt style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.createModal.clubsLabel")}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{selectedClubIds.length}</dd>
            </dl>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={secondaryBtn} onClick={() => setStage("gameweeks")}>{t("common.back")}</button>
              <button style={primaryBtn} disabled={creating} onClick={handleCreate}>
                {creating ? t("common.saving") : t("admin.competitions.createModal.submit")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
